import React, { useEffect, useState } from 'react';
import { ChevronDown, Shield, Home, FileCheck } from 'lucide-react';

const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToNext = () => {
    const element = document.querySelector('#benefits');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center hero-pattern overflow-hidden">
      {/* Subtle animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#c9a87c]/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#c9a87c]/3 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="text-left">
            {/* Badge */}
            <div 
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1a1a1a] border border-[#c9a87c]/20 mb-8 ${
                isLoaded ? 'animate-fade-in-up' : 'opacity-0'
              }`}
            >
              <span className="w-2 h-2 bg-[#c9a87c] rounded-full animate-pulse" />
              <span className="font-sans text-xs tracking-widest uppercase text-[#c9a87c]">Florida Estate Planning</span>
            </div>

            {/* Headline */}
            <h1 
              className={`font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-medium leading-tight mb-6 ${
                isLoaded ? 'animate-fade-in-up delay-100' : 'opacity-0'
              }`}
            >
              <span className="text-[#faf9f7]">Florida </span>
              <span className="text-gradient-gold">Lady Bird Deed</span>
              <span className="text-[#faf9f7]"> Attorney</span>
            </h1>

            {/* Subheadline */}
            <p 
              className={`font-sans text-lg md:text-xl text-[#faf9f7]/70 max-w-xl mb-8 leading-relaxed ${
                isLoaded ? 'animate-fade-in-up delay-200' : 'opacity-0'
              }`}
            >
              Protect your Florida home and avoid probate with a Lady Bird Deed. 
              Maintain full control of your property while ensuring a seamless transfer to your heirs.
            </p>

            {/* CTA Buttons */}
            <div 
              className={`flex flex-col sm:flex-row gap-4 mb-12 ${
                isLoaded ? 'animate-fade-in-up delay-300' : 'opacity-0'
              }`}
            >
              <button
                onClick={scrollToContact}
                className="btn-elegant px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold tracking-wide rounded-sm hover:bg-[#d4b896] animate-pulse-glow"
              >
                Schedule Free Consultation
              </button>
              <a
                href="#how-it-works"
                className="btn-elegant px-8 py-4 border border-[#c9a87c]/40 text-[#faf9f7] font-sans font-medium tracking-wide rounded-sm hover:border-[#c9a87c] hover:bg-[#c9a87c]/10 text-center"
              >
                Learn How It Works
              </a>
            </div>

            {/* Trust indicators */}
            <div 
              className={`flex flex-wrap gap-6 ${
                isLoaded ? 'animate-fade-in-up delay-400' : 'opacity-0'
              }`}
            >
              <div className="flex items-center gap-2 text-[#faf9f7]/60">
                <Shield className="w-4 h-4 text-[#c9a87c]" />
                <span className="font-sans text-sm">Avoid Probate</span>
              </div>
              <div className="flex items-center gap-2 text-[#faf9f7]/60">
                <Home className="w-4 h-4 text-[#c9a87c]" />
                <span className="font-sans text-sm">Keep Full Control</span>
              </div>
              <div className="flex items-center gap-2 text-[#faf9f7]/60">
                <FileCheck className="w-4 h-4 text-[#c9a87c]" />
                <span className="font-sans text-sm">Florida Compliant</span>
              </div>
            </div>
          </div>

          {/* Visual Element */}
          <div 
            className={`relative hidden lg:block ${
              isLoaded ? 'animate-scale-in delay-300' : 'opacity-0'
            }`}
          >
            <div className="relative">
              {/* Decorative frame */}
              <div className="absolute -inset-4 border border-[#c9a87c]/20 rounded-lg" />
              <div className="absolute -inset-8 border border-[#c9a87c]/10 rounded-lg" />
              
              {/* Main visual card */}
              <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-lg p-10 border border-[#c9a87c]/20">
                <div className="space-y-6">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-full bg-[#c9a87c]/10 flex items-center justify-center">
                    <Home className="w-8 h-8 text-[#c9a87c]" />
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="font-serif text-2xl text-[#faf9f7] italic leading-relaxed">
                    "A Lady Bird Deed is the simplest way to pass your Florida home to your loved ones."
                  </blockquote>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-6 pt-6 border-t border-[#c9a87c]/10">
                    <div>
                      <div className="font-serif text-3xl text-[#c9a87c]">100%</div>
                      <div className="font-sans text-sm text-[#faf9f7]/60">Control Retained</div>
                    </div>
                    <div>
                      <div className="font-serif text-3xl text-[#c9a87c]">$0</div>
                      <div className="font-sans text-sm text-[#faf9f7]/60">Probate Cost</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button 
        onClick={scrollToNext}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce cursor-pointer"
        aria-label="Scroll to benefits"
      >
        <ChevronDown className="w-8 h-8 text-[#c9a87c]/60" />
      </button>
    </section>
  );
};

export default HeroSection;
